import {
  listUsers,
  updateUserPlan,
  updateUserRole,
  updateUserUiVersion,
  createUser,
  updateUserProfile,
  updateUserPassword,
  deleteUser,
} from '../auth.js';
import { PLANS, DEFAULT_PLAN_ID, getPlanById } from '../plans.js';
import { getAdminCopy } from '../i18n.js';

const html = String.raw;

const config = window.__ANFORDERUNGSAI_CONFIG__ ?? {};

const formatCopy = (template, replacements = {}) => {
  if (!template) return '';
  return template.replace(/{{(.*?)}}/g, (_, key) => {
    const value = replacements[key.trim()];
    return value === undefined || value === null ? '' : value;
  });
};

const formatDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const escapeAttr = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');

const resolveStripeDashboardBaseUrl = () => {
  const raw = typeof config.stripeDashboardBaseUrl === 'string' ? config.stripeDashboardBaseUrl.trim() : '';
  if (raw.startsWith('http')) {
    return raw.replace(/\/$/, '');
  }
  const mode = typeof config.stripeDashboardMode === 'string' ? config.stripeDashboardMode.trim().toLowerCase() : '';
  if (mode === 'test') {
    return 'https://dashboard.stripe.com/test';
  }
  return 'https://dashboard.stripe.com';
};

const stripeDashboardBaseUrl = resolveStripeDashboardBaseUrl();

const renderStripeCustomerLink = (user, copy) => {
  if (!user?.stripeCustomerId) {
    return `<span class="muted small">${copy.stripe.none}</span>`;
  }
  const customerId = escapeAttr(user.stripeCustomerId);
  const href = `${stripeDashboardBaseUrl}/customers/${customerId}`;
  return `<a href="${href}" target="_blank" rel="noopener">${copy.stripe.open}</a>`;
};

const renderProfileSummary = (user, copy) => {
  const profile = user.profile ?? {};
  const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(' ');
  const lines = [
    fullName ? `<strong>${fullName}</strong>` : `<strong>${copy.table.profileFallback}</strong>`,
  ];
  if (profile.company) lines.push(`<div class="muted small">${profile.company}</div>`);
  return `<div class="profile-summary">${lines.join('')}</div>`;
};

const renderPlanLabel = (user, copy) => {
  const plan = user.plan ?? getPlanById(user.planId);
  if (!plan) {
    return `<span class="muted">${copy.plan.unknown}</span>`;
  }
  return html`
    <div class="stack" style="gap:4px;">
      <strong>${plan.name}</strong>
    </div>
  `;
};

const renderRoleLabel = (role, copy) => {
  let label = copy.roles.user;
  if (role === 'admin') label = copy.roles.admin;
  if (role === 'appEntwickler') label = copy.roles.appEntwickler || 'App Entwickler';
  return `<span class="pill" style="background-color: rgba(148,163,184,0.25); color:#e2e8f0;">${label}</span>`;
};

const formatMonthLabel = (monthKey) => {
  if (!monthKey) return '—';
  const [year, month] = String(monthKey).split('-').map((value) => Number(value));
  if (!year || !month) return monthKey;
  const date = new Date(Date.UTC(year, month - 1, 1));
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const renderJobStatsTable = (jobStats, copy) => {
  const monthly = Array.isArray(jobStats?.monthly) ? jobStats.monthly : [];
  if (!monthly.length) {
    return `<p class="muted small">${copy.jobStats?.empty ?? '—'}</p>`;
  }
  return html`
    <table>
      <thead>
        <tr>
          <th>${copy.jobStats?.columns?.month ?? 'Monat'}</th>
          <th>${copy.jobStats?.columns?.count ?? 'Jobs'}</th>
        </tr>
      </thead>
      <tbody>
        ${monthly
          .map((entry) => html`
            <tr>
              <td>${formatMonthLabel(entry.month)}</td>
              <td>${entry.count ?? 0}</td>
            </tr>
          `)
          .join('')}
      </tbody>
    </table>
  `;
};

const renderPlanSelectOptions = (selectedPlan) =>
  PLANS.map((plan) => `<option value="${plan.id}" ${plan.id === selectedPlan ? 'selected' : ''}>${plan.name}</option>`).join('');

const renderPlanOptions = (selectedPlan) => renderPlanSelectOptions(selectedPlan);

const renderRoleOptions = (selectedRole, copy) => html`
  <option value="user" ${selectedRole === 'user' ? 'selected' : ''}>${copy.roles.user}</option>
  <option value="appEntwickler" ${selectedRole === 'appEntwickler' ? 'selected' : ''}>${copy.roles.appEntwickler || 'App Entwickler'}</option>
  <option value="admin" ${selectedRole === 'admin' ? 'selected' : ''}>${copy.roles.admin}</option>
`;

const renderUiVersionOptions = (selectedVersion, copy) => {
  const versions = [1, 2, 3];
  return versions
    .map((version) => {
      const labelTemplate = copy.uiVersion?.optionLabel ?? 'Version {{version}}';
      const label = formatCopy(labelTemplate, { version });
      return `<option value="${version}" ${version === selectedVersion ? 'selected' : ''}>${label}</option>`;
    })
    .join('');
};

const renderUiVersionLabel = (uiVersion, copy) => {
  const version = Number(uiVersion) || 1;
  const labelTemplate = copy.uiVersion?.badgeLabel ?? 'V{{version}}';
  const label = formatCopy(labelTemplate, { version });
  const palette = {
    2: 'background-color: rgba(59,130,246,0.2); color: #bfdbfe;',
    3: 'background-color: rgba(249,115,22,0.2); color: #fed7aa;',
  };
  const style = palette[version] ?? 'background-color: rgba(148,163,184,0.25); color:#e2e8f0;';
  return `<span class="pill" style="${style}">${label}</span>`;
};

const createModalState = (user) => ({
  email: user.email,
  planId: user.planId ?? DEFAULT_PLAN_ID,
  role: user.role ?? 'user',
  uiVersion: Number(user.uiVersion) || 1,
  firstName: user.profile?.firstName ?? '',
  lastName: user.profile?.lastName ?? '',
  company: user.profile?.company ?? '',
  phone: user.profile?.phone ?? '',
  vatNumber: user.profile?.vatNumber ?? '',
  street: user.profile?.address?.street ?? '',
  postalCode: user.profile?.address?.postalCode ?? '',
  city: user.profile?.address?.city ?? '',
  country: user.profile?.address?.country ?? '',
  jobStats: user.jobStats ?? { total: 0, currentMonth: 0, monthly: [] },
  status: '',
});

const parseEditEmailFromHash = () => {
  const hash = window.location.hash ?? '';
  const query = hash.includes('?') ? hash.split('?')[1] : '';
  if (!query) return '';
  const params = new URLSearchParams(query);
  return (params.get('edit') || '').trim().toLowerCase();
};

const renderEditModal = (modal, copy) => html`
  <div class="modal-backdrop" id="admin-edit-modal">
    <div class="card modal-card stack admin-only-surface admin-only-page" style="gap:16px;">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <h3>${copy.modal.title}</h3>
        <button type="button" class="ghost-button" data-action="close">${copy.actions.close}</button>
      </div>
      <div class="muted small">${modal.email}</div>
      <form class="stack" id="admin-edit-form">
        <label>
          ${copy.form.plan}
          <select name="planId">
            ${renderPlanSelectOptions(modal.planId ?? DEFAULT_PLAN_ID)}
          </select>
        </label>
        <label>
          ${copy.form.role}
          <select name="role">
            ${renderRoleOptions(modal.role ?? 'user', copy)}
          </select>
        </label>
        <label>
          ${copy.form.uiVersion ?? 'UI-Version'}
          <select name="uiVersion">
            ${renderUiVersionOptions(modal.uiVersion ?? 1, copy)}
          </select>
        </label>
        <div class="row">
          <label style="flex:1;">
            ${copy.form.firstName}
            <input type="text" name="firstName" value="${escapeAttr(modal.firstName)}" />
          </label>
          <label style="flex:1;">
            ${copy.form.lastName}
            <input type="text" name="lastName" value="${escapeAttr(modal.lastName)}" />
          </label>
        </div>
        <label>
          ${copy.form.company}
          <input type="text" name="company" value="${escapeAttr(modal.company)}" />
        </label>
        <label>
          ${copy.form.phone}
          <input type="tel" name="phone" value="${escapeAttr(modal.phone)}" />
        </label>
        <label>
          ${copy.form.vat}
          <input type="text" name="vatNumber" value="${escapeAttr(modal.vatNumber)}" />
        </label>
        <div class="row">
          <label style="flex:1;">
            ${copy.form.street}
            <input type="text" name="street" value="${escapeAttr(modal.street)}" />
          </label>
          <label style="flex:0.5;">
            ${copy.form.postalCode}
            <input type="text" name="postalCode" value="${escapeAttr(modal.postalCode)}" />
          </label>
          <label style="flex:1;">
            ${copy.form.city}
            <input type="text" name="city" value="${escapeAttr(modal.city)}" />
          </label>
        </div>
        <label>
          ${copy.form.country}
          <input type="text" name="country" value="${escapeAttr(modal.country)}" />
        </label>
        <label>
          ${copy.form.newPassword}
          <input type="password" name="newPassword" minlength="6" autocomplete="new-password" />
        </label>
        <label>
          ${copy.form.confirmPassword}
          <input type="password" name="confirmPassword" minlength="6" autocomplete="new-password" />
        </label>
        <div class="row" style="gap:12px; flex-wrap: wrap; align-items: center;">
          <div class="row" style="gap:12px; flex-wrap: wrap;">
            <button type="submit">${copy.actions.save}</button>
            <button type="button" class="ghost-button" data-action="close">${copy.actions.cancel}</button>
          </div>
          <button
            type="button"
            class="ghost-button"
            data-action="delete"
            style="margin-left:auto; border-color: rgba(239,68,68,0.5); color:#fca5a5;"
          >
            ${copy.actions.delete}
          </button>
        </div>
      </form>
      <div class="stack" style="gap:8px;">
        <h4 style="margin:0;">${copy.jobStats?.title ?? 'Jobs'}</h4>
        ${renderJobStatsTable(modal.jobStats, copy)}
      </div>
      <p class="muted" id="modal-status">${modal.status ?? ''}</p>
    </div>
  </div>
`;

const renderUserRow = (user, copy) => {
  const jobStats = user.jobStats ?? {};
  const currentMonthJobs = Number(jobStats.currentMonth ?? 0);
  const totalJobs = Number(jobStats.total ?? 0);
  return html`
  <tr data-email="${user.email}">
    <td class="admin-col-profile">${renderProfileSummary(user, copy)}</td>
    <td class="admin-col-registered">${formatDate(user.createdAt)}</td>
    <td class="admin-col-plan">${renderPlanLabel(user, copy)}</td>
    <td class="admin-col-role">${renderRoleLabel(user.role, copy)}</td>
    <td class="admin-col-ui-version">${renderUiVersionLabel(user.uiVersion, copy)}</td>
    <td class="admin-col-stripe">${renderStripeCustomerLink(user, copy)}</td>
    <td class="admin-col-jobs-month">${currentMonthJobs}</td>
    <td class="admin-col-jobs-total">${totalJobs}</td>
    <td class="admin-col-actions"><button type="button" data-action="edit">${copy.table.editButton}</button></td>
  </tr>
`;
};

export const renderAdminView = (container) => {
  const copy = getAdminCopy();
  const state = {
    showAddUser: false,
    modal: null,
    users: [],
    loading: false,
    flash: '',
    autoOpenEmail: parseEditEmailFromHash(),
    autoOpenDone: false,
  };

  const render = (flashMessage = '') => {
    if (typeof flashMessage === 'string' && flashMessage) {
      state.flash = flashMessage;
    }
    const users = state.users;
    const isLoading = state.loading;
    container.innerHTML = html`
      <div class="card stack admin-only-surface admin-only-page">
        <div class="row" style="justify-content: space-between; align-items: center;">
          <h2>${copy.title}</h2>
          <button type="button" id="toggle-add-user">
            ${state.showAddUser ? copy.toggle.close : copy.toggle.open}
          </button>
        </div>
        <p class="muted">
          ${copy.description}
        </p>
        ${
          state.showAddUser
            ? html`
                <section class="stack admin-add-user">
                  <h3>${copy.addUser.title}</h3>
                  <form class="stack" id="admin-add-user">
                    <div class="row">
                      <label style="flex:1;">
                        ${copy.form.firstName}
                        <input type="text" name="firstName" autocomplete="given-name" />
                      </label>
                      <label style="flex:1;">
                        ${copy.form.lastName}
                        <input type="text" name="lastName" autocomplete="family-name" />
                      </label>
                    </div>
                    <div class="row">
                      <label style="flex:1;">
                        ${copy.form.email}
                        <input type="email" name="email" required autocomplete="email" />
                      </label>
                      <label style="flex:1;">
                        ${copy.form.password}
                        <input type="password" name="password" required minlength="6" />
                      </label>
                    </div>
                    <label>
                      ${copy.form.company}
                      <input type="text" name="company" placeholder="${copy.placeholders.company}" />
                    </label>
                    <label>
                      ${copy.form.phone}
                      <input type="tel" name="phone" placeholder="${copy.placeholders.phone}" required />
                    </label>
                    <label>
                      ${copy.form.vat}
                      <input type="text" name="vatNumber" placeholder="${copy.placeholders.vat}" />
                    </label>
                    <div class="row">
                      <label style="flex:1;">
                        ${copy.form.street}
                        <input type="text" name="street" placeholder="${copy.placeholders.street}" />
                      </label>
                      <label style="flex:0.5;">
                        ${copy.form.postalCode}
                        <input type="text" name="postalCode" placeholder="${copy.placeholders.postalCode}" />
                      </label>
                      <label style="flex:1;">
                        ${copy.form.city}
                        <input type="text" name="city" placeholder="${copy.placeholders.city}" />
                      </label>
                    </div>
                    <label>
                      ${copy.form.country}
                      <input type="text" name="country" placeholder="${copy.placeholders.country}" />
                    </label>
                    <div class="row" style="flex-wrap: wrap;">
                      <label style="flex:1;">
                        ${copy.form.plan}
                        <select name="planId">
                          ${renderPlanOptions(DEFAULT_PLAN_ID)}
                        </select>
                      </label>
                      <label style="flex:1;">
                        ${copy.form.role}
                        <select name="role">
                          ${renderRoleOptions('user', copy)}
                        </select>
                      </label>
                      <label style="flex:1;">
                        ${copy.form.uiVersion ?? 'UI-Version'}
                        <select name="uiVersion">
                          ${renderUiVersionOptions(1, copy)}
                        </select>
                      </label>
                    </div>
                    <button type="submit">${copy.addUser.submit}</button>
                  </form>
                </section>
              `
            : ''
        }
        <section>
          ${isLoading
            ? `<p class="muted">${copy.loading}</p>`
            : users.length
                ? html`
                    <table class="admin-table">
                      <thead>
                        <tr>
                          <th class="admin-col-profile">${copy.table.headers.profile}</th>
                          <th class="admin-col-registered">${copy.table.headers.registered}</th>
                          <th class="admin-col-plan">${copy.table.headers.plan}</th>
                          <th class="admin-col-role">${copy.table.headers.role}</th>
                          <th class="admin-col-ui-version">${copy.table.headers.uiVersion ?? 'UI-Version'}</th>
                          <th class="admin-col-stripe">${copy.table.headers.stripe}</th>
                          <th class="admin-col-jobs-month">
                            <div class="stack tight">
                              <span>${copy.table.headers.jobs ?? 'Jobs'}</span>
                              <span class="muted small">${copy.table.headers.jobsCurrentMonthSub ?? copy.table.headers.jobsCurrentMonth}</span>
                            </div>
                          </th>
                          <th class="admin-col-jobs-total">
                            <div class="stack tight">
                              <span>${copy.table.headers.jobs ?? 'Jobs'}</span>
                              <span class="muted small">${copy.table.headers.jobsTotalSub ?? copy.table.headers.jobsTotal}</span>
                            </div>
                          </th>
                          <th class="admin-col-actions">${copy.table.headers.actions}</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${users.map((user) => renderUserRow(user, copy)).join('')}
                      </tbody>
                    </table>
                  `
                : `<p class="muted">${copy.empty}</p>`}
        </section>
        <p class="muted" id="admin-status">${state.flash}</p>
      </div>
      ${state.modal ? renderEditModal(state.modal, copy) : ''}
    `;

    const statusEl = container.querySelector('#admin-status');
    const toggleBtn = container.querySelector('#toggle-add-user');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        state.showAddUser = !state.showAddUser;
        render();
      });
    }

    const setStatus = (message, autoClear = true) => {
      if (!statusEl) return;
      state.flash = message;
      statusEl.textContent = message;
      if (message && autoClear) {
        setTimeout(() => {
          if (statusEl.textContent === message) {
            state.flash = '';
            statusEl.textContent = '';
          }
        }, 2000);
      }
    };

    if (flashMessage) {
      setStatus(flashMessage);
    }

    const addForm = state.showAddUser ? container.querySelector('#admin-add-user') : null;
    if (addForm) {
      addForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const formData = new FormData(addForm);
        const firstName = formData.get('firstName')?.toString().trim() ?? '';
        const lastName = formData.get('lastName')?.toString().trim() ?? '';
        const email = formData.get('email')?.toString().trim() ?? '';
        const password = formData.get('password')?.toString() ?? '';
        const company = formData.get('company')?.toString().trim() ?? '';
        const phone = formData.get('phone')?.toString().trim() ?? '';
        const vatNumber = formData.get('vatNumber')?.toString().trim() ?? '';
        const street = formData.get('street')?.toString().trim() ?? '';
        const postalCode = formData.get('postalCode')?.toString().trim() ?? '';
        const city = formData.get('city')?.toString().trim() ?? '';
        const country = formData.get('country')?.toString().trim() ?? '';
        const planId = formData.get('planId')?.toString() || DEFAULT_PLAN_ID;
        const role = formData.get('role')?.toString() || 'user';
        const uiVersion = Number(formData.get('uiVersion')?.toString() || '1');

        if (!email || !password) {
          setStatus(copy.statuses.createRequired);
          return;
        }
        if (![1, 2, 3].includes(uiVersion)) {
          setStatus(copy.uiVersion?.invalid ?? 'Ungültige UI-Version.');
          return;
        }

        try {
          await createUser({
            email,
            password,
            planId,
            role,
            uiVersion,
            firstName,
            lastName,
            company,
            phone,
            vatNumber,
            street,
            postalCode,
            city,
            country,
          });
          state.showAddUser = false;
          await loadUsers(formatCopy(copy.statuses.createSuccess, { email }));
          return;
        } catch (error) {
          console.error(error);
          setStatus(error instanceof Error ? error.message : copy.statuses.createFailed);
        }
      });
    }

    container.querySelectorAll('[data-action="edit"]').forEach((button) => {
      const row = button.closest('tr');
      if (!row) return;
      const email = row.dataset.email;
      const user = users.find((entry) => entry.email === email);
      if (!user) return;
      button.addEventListener('click', () => {
        state.modal = createModalState(user);
        render();
      });
    });

    const modalEl = container.querySelector('#admin-edit-modal');
    if (modalEl && state.modal) {
      const closeModal = () => {
        state.modal = null;
        render();
      };

      modalEl.addEventListener('click', (event) => {
        if (event.target === modalEl) {
          closeModal();
        }
      });

      modalEl.querySelectorAll('[data-action="close"]').forEach((btn) => {
        btn.addEventListener('click', closeModal);
      });

      const statusEl = modalEl.querySelector('#modal-status');
      const deleteButton = modalEl.querySelector('[data-action="delete"]');
      const form = modalEl.querySelector('#admin-edit-form');

      if (deleteButton && statusEl) {
        deleteButton.addEventListener('click', async () => {
          if (!state.modal || !state.modal.email) {
            return;
          }
          const confirmed = window.confirm(copy.statuses.deleteConfirm);
          if (!confirmed) {
            return;
          }
          const targetEmail = state.modal.email;
          statusEl.textContent = copy.statuses.deleteInProgress;
          deleteButton.disabled = true;
          if (form) {
            form.querySelectorAll('button').forEach((btn) => {
              btn.disabled = true;
            });
          }
          try {
            await deleteUser(targetEmail);
            state.modal = null;
            await loadUsers(formatCopy(copy.statuses.deleteSuccess, { email: targetEmail }));
          } catch (error) {
            console.error(error);
            const message = error instanceof Error ? error.message : copy.statuses.deleteFailed;
            if (state.modal) {
              state.modal.status = message;
              statusEl.textContent = message;
            } else {
              setStatus(message, false);
            }
          } finally {
            if (state.modal) {
              deleteButton.disabled = false;
              if (form) {
                form.querySelectorAll('button').forEach((btn) => {
                  btn.disabled = false;
                });
              }
            }
          }
        });
      }

      if (form && statusEl) {
        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          const formData = new FormData(form);
          const planId = formData.get('planId')?.toString() || state.modal.planId;
          const role = formData.get('role')?.toString() || state.modal.role;
          const uiVersion = Number(formData.get('uiVersion')?.toString() || String(state.modal.uiVersion ?? 1));
          const firstName = formData.get('firstName')?.toString().trim() ?? '';
          const lastName = formData.get('lastName')?.toString().trim() ?? '';
          const company = formData.get('company')?.toString().trim() ?? '';
          const phone = formData.get('phone')?.toString().trim() ?? '';
          const vatNumber = formData.get('vatNumber')?.toString().trim() ?? '';
          const street = formData.get('street')?.toString().trim() ?? '';
          const postalCode = formData.get('postalCode')?.toString().trim() ?? '';
          const city = formData.get('city')?.toString().trim() ?? '';
          const country = formData.get('country')?.toString().trim() ?? '';
          const newPassword = formData.get('newPassword')?.toString() ?? '';
          const confirmPassword = formData.get('confirmPassword')?.toString() ?? '';
          const wantsPasswordChange =
            newPassword.trim().length > 0 || confirmPassword.trim().length > 0;

          if (wantsPasswordChange) {
            if (newPassword.length < 6) {
              const message = copy.statuses.passwordTooShort;
              state.modal.status = message;
              statusEl.textContent = message;
              return;
            }
            if (newPassword !== confirmPassword) {
              const message = copy.statuses.passwordMismatch;
              state.modal.status = message;
              statusEl.textContent = message;
              return;
            }
          }
          if (![1, 2, 3].includes(uiVersion)) {
            const message = copy.uiVersion?.invalid ?? 'Ungültige UI-Version.';
            state.modal.status = message;
            statusEl.textContent = message;
            return;
          }

          state.modal.status = copy.statuses.updateSaving;
          statusEl.textContent = state.modal.status;
          form.querySelectorAll('button').forEach((btn) => {
            btn.disabled = true;
          });

          try {
            if (wantsPasswordChange) {
              await updateUserPassword({ email: state.modal.email, password: newPassword });
            }
            if (planId !== state.modal.planId) {
              await updateUserPlan({ email: state.modal.email, planId });
            }
            if (role !== state.modal.role) {
              await updateUserRole({ email: state.modal.email, role });
            }
            if (uiVersion !== Number(state.modal.uiVersion ?? 1)) {
              await updateUserUiVersion({ email: state.modal.email, uiVersion });
            }
            await updateUserProfile({
              email: state.modal.email,
              firstName,
              lastName,
              company,
              phone,
              vatNumber,
              street,
              postalCode,
              city,
              country,
            });
            const targetEmail = state.modal.email;
            state.modal = null;
            await loadUsers(formatCopy(copy.statuses.updateSuccess, { email: targetEmail }));
            return;
          } catch (error) {
            console.error(error);
            const message = error instanceof Error ? error.message : copy.statuses.updateFailed;
            if (state.modal) {
              state.modal.status = message;
              statusEl.textContent = state.modal.status;
            } else {
              setStatus(message, false);
            }
          } finally {
            form.querySelectorAll('button').forEach((btn) => {
              btn.disabled = false;
            });
          }
        });
      }
    }
  };

  const loadUsers = async (flashMessage = '') => {
    state.loading = true;
    if (typeof flashMessage === 'string' && flashMessage) {
      state.flash = flashMessage;
    }
    render();
    try {
      const users = await listUsers();
      state.users = users;
      state.loading = false;
      if (state.autoOpenEmail && !state.autoOpenDone) {
        const target = users.find((entry) => entry.email?.toLowerCase() === state.autoOpenEmail);
        state.autoOpenDone = true;
        if (target) {
          state.modal = createModalState(target);
        }
      }
      render(state.flash);
    } catch (error) {
      state.loading = false;
      state.users = [];
      const message = error instanceof Error ? error.message : copy.statuses.loadFailed;
      render(message);
    }
  };

  render();
  loadUsers();
};
